import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import CodeOutputBuilderNodeView from './code-output-builder-node-view';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        codeOutputBuilder: {
            insertCodeOutputBuilder: (options?: InsertCodeOutputBuilderOptions) => ReturnType;
        };
    }
}

export interface InsertCodeOutputBuilderOptions {
    id?: string;
    caption?: string;
    codeOutputXMLToLoad?: string;
    options?: { [key: string]: any };
    parts?: number;
}

export const CodeOutputBuilderNodeHeadless = Node.create({
    name: 'codeOutputBuilder',

    group: 'block',

    atom: true,

    selectable: true,

    draggable: true,

    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => element.getAttribute('data-code-output-builder-id'),
                renderHTML: attributes => ({
                    'data-code-output-builder-id': attributes.id,
                }),
            },
            caption: {
                default: '',
                parseHTML: element => element.getAttribute('data-caption') || '',
                renderHTML: attributes => ({
                    'data-caption': attributes.caption,
                }),
            },
            codeOutputXMLToLoad: {
                default: '',
                parseHTML: element => element.getAttribute('data-code-output-xml') || '',
                renderHTML: attributes => ({
                    'data-code-output-xml': attributes.codeOutputXMLToLoad,
                }),
            },
            options: {
                default: {},
                parseHTML: element => {
                    const raw = element.getAttribute('data-options');
                    try {
                        return raw ? JSON.parse(raw) : {};
                    } catch (error) {
                        console.error('Failed to parse data-options:', error, raw);
                        return {};
                    }
                },
                renderHTML: attributes => ({
                    'data-options': JSON.stringify(attributes.options),
                }),
            },
            parts: {
                default: 1,
                parseHTML: element => parseInt(element.getAttribute('data-parts') || '1', 10),
                renderHTML: attributes => ({
                    'data-parts': attributes.parts,
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="code-output-builder"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-type': 'code-output-builder',
                class: 'codeoutput-builder-container',
            }),
        ];
    },

    addCommands() {
        return {
            insertCodeOutputBuilder:
                (options: InsertCodeOutputBuilderOptions = {}) =>
                ({ commands }) => {
                    const emptyCodeOutputXML = `<zyTool name='codeOutput' caption='' id='' parts='1' challenge='true'>
<zyOptions>
<language>cpp</language>
<levels type='list'>
<level type='dict'>
    <explanation></explanation>
    <template></template>
    <parameters type='dict'></parameters>
    <input></input>
</level>
</levels>
<languageOption>Classic C++</languageOption>
</zyOptions>
</zyTool>`;

                    return commands.insertContent({
                        type: this.name,
                        attrs: {
                            caption: options.caption || '',
                            codeOutputXMLToLoad: options.codeOutputXMLToLoad || emptyCodeOutputXML,
                            options: options.options || {},
                            parts: options.parts || 1,
                        },
                    });
                },
        };
    },
});

export const CodeOutputBuilderNode = CodeOutputBuilderNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(CodeOutputBuilderNodeView);
    },
});

export default CodeOutputBuilderNode;
